import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Hash, Plus, Trash } from "@phosphor-icons/react";

export default function HashtagMaster() {
  const [tags, setTags] = useState([]);
  const [name, setName] = useState("");
  const [keywords, setKeywords] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/hashtags");
    setTags(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/hashtags", {
        tag_name: name.replace(/^#/, "").trim().toLowerCase(),
        keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        description,
      });
      toast.success(`#${name} added`);
      setName("");
      setKeywords("");
      setDescription("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, n) => {
    if (!window.confirm(`Delete #${n}?`)) return;
    await api.delete(`/hashtags/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-8" data-testid="hashtag-master-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// indexing dictionary</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          Hashtag Master
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          The shared dictionary that gives every fact a discoverable handle. No duplicates allowed.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="border border-slate-200 bg-white p-6 space-y-4 lg:col-span-1" data-testid="hashtag-form">
          <div className="mono-label">New hashtag</div>
          <div>
            <label className="mono-label block mb-1">#Tag name</label>
            <input
              data-testid="hashtag-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="pricing"
              className="w-full border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-slate-900"
              style={{ borderRadius: 2 }}
            />
          </div>
          <div>
            <label className="mono-label block mb-1">Keywords (comma separated)</label>
            <input
              data-testid="hashtag-keywords-input"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="mrp, cost, price"
              className="w-full border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-slate-900"
              style={{ borderRadius: 2 }}
            />
          </div>
          <div>
            <label className="mono-label block mb-1">Description</label>
            <textarea
              data-testid="hashtag-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What kind of content this tag covers"
              className="w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900 resize-none"
              style={{ borderRadius: 2 }}
            />
          </div>
          <button type="submit" disabled={busy} className="w-full btn-sharp btn-primary disabled:opacity-50" data-testid="create-hashtag-btn">
            <Plus size={16} weight="bold" />
            {busy ? "Adding…" : "Add to master"}
          </button>
        </form>

        <div className="lg:col-span-2 border border-slate-200 bg-white" data-testid="hashtag-list">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="mono-label">Existing tags</div>
            <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200">
              {tags.length}
            </span>
          </div>
          {tags.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">No hashtags yet</div>
          ) : (
            <div>
              {tags.map((t) => (
                <div
                  key={t.id}
                  className="flex items-start justify-between border-b border-slate-100 px-5 py-4 hover:bg-slate-50 transition-all"
                  data-testid={`hashtag-row-${t.tag_name}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Hash size={14} className="text-orange-600" />
                      <span className="font-mono text-sm font-semibold text-slate-900">
                        #{t.tag_name}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600 mb-2">{t.description || "—"}</div>
                    <div className="flex flex-wrap gap-1">
                      {t.keywords.map((k) => (
                        <span key={k} className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700">
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(t.id, t.tag_name)}
                    className="text-slate-400 hover:text-orange-600 transition-all"
                    data-testid={`delete-hashtag-${t.tag_name}`}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
